/**
 * LES DIAGRAMMES MANIPULÉS — atelier de figures, palier L5 (F7 et F9 ;
 * `docs/pilotage/atelier-figures/CONCEPTION.md` § 2, § 3).
 *
 * Ce module relie trois choses que l'application, le serveur et la console
 * partagent :
 *
 *  1. L'ÉCRITURE DE LA RÉPONSE d'un diagramme, ÉTENDUE sans rien casser : les
 *     éléments s'écrivent toujours `0:rang=valeur` (`CAP-08`) ; une droite à
 *     l'œil ajoute `droite=x1|y1|x2|y2`, un graphe `aretes=…`, `couleurs=…`,
 *     `sommets=…`, `liens=…`. Les segments sont séparés par « ; ».
 *  2. L'AIMANT d'une barre ou d'un point qu'on glisse : la graduation de l'axe
 *     (ou une fraction ronde de son pas), jamais la réponse.
 *  3. LE DESSIN APRÈS LA CORRECTION : la bande acceptée et le point moyen d'une
 *     droite à l'œil ; le parcours numéroté, les couleurs et l'arête fautive
 *     désignée d'un graphe. Ce sont des TRACES du moteur des fiches : l'app et
 *     la console les peignent sans une ligne de plus. Jamais de rouge : ce qui
 *     est désigné l'est en ardoise.
 */
import { lireRationnel } from '../math/ensembles.js';
import { droiteDesPoignees } from './ajustement.js';
import { direDiagramme, elementsDe, geometrieDuDiagramme, resoudreDiagramme, versToile, } from './diagramme.js';
import { ecrireReponseDeGraphe, lireSegmentDeGraphe, } from './graphe.js';
const nombreEcrit = (v) => String(Number(v.toFixed(6))).replace('.', ',');
/** La réponse d'un diagramme, écrite : `0:2=7 ; droite=1|2|9|8,5 ; aretes=0,3`. */
export function ecrireReponseDeDiagramme(r) {
    const segments = (r.cases ?? [])
        .filter((c) => c.valeur.trim() !== '')
        .map((c) => `0:${c.rang}=${c.valeur.trim()}`);
    if (r.droite !== undefined && r.droite !== null) {
        const { x1, y1, x2, y2 } = r.droite;
        segments.push(`droite=${[x1, y1, x2, y2].map(nombreEcrit).join('|')}`);
    }
    if (r.graphe !== undefined && r.graphe !== null)
        segments.push(...ecrireReponseDeGraphe(r.graphe));
    return segments.join(' ; ');
}
/** Lit ce que `ecrireReponseDeDiagramme` écrit. `null` : ce n'est pas cette écriture. */
export function lireReponseDeDiagramme(texte) {
    const cases = new Map();
    let droite = null;
    const graphe = { r: {} };
    let avecGraphe = false;
    const brut = (texte ?? '').trim();
    if (brut === '')
        return { cases, droite, graphe: null };
    for (const morceau of brut.split(';')) {
        const c = /^\s*(\d{1,2}):(\d{1,2})\s*=\s*(.+?)\s*$/.exec(morceau);
        if (c !== null) {
            const cle = `${Number(c[1])}:${Number(c[2])}`;
            if (cases.has(cle))
                return null;
            cases.set(cle, c[3]);
            continue;
        }
        const d = /^\s*droite\s*=\s*(.+?)\s*$/.exec(morceau);
        if (d !== null) {
            const nombres = d[1].split('|').map((x) => lireRationnel(x.trim()));
            if (droite !== null || nombres.length !== 4 || nombres.some((n) => n === null))
                return null;
            const [x1, y1, x2, y2] = nombres.map((n) => n.n / n.d);
            droite = { x1, y1, x2, y2 };
            continue;
        }
        const g = lireSegmentDeGraphe(morceau, graphe);
        if (g !== true)
            return null;
        avecGraphe = true;
    }
    return { cases, droite, graphe: avecGraphe ? graphe.r : null };
}
/** La réponse a-t-elle quelque chose à corriger ? */
export function reponseDeDiagrammeDonnee(r) {
    if (r === null)
        return false;
    const g = r.graphe;
    return (r.cases.size > 0 ||
        r.droite !== null ||
        (g !== null &&
            ((g.aretes?.length ?? 0) > 0 ||
                Object.keys(g.couleurs ?? {}).length > 0 ||
                (g.sommets?.length ?? 0) > 0 ||
                (g.liens?.length ?? 0) > 0)));
}
/* ─────────────────────────────── l'aimant ───────────────────────────────── */
/** Les diagrammes dont on GLISSE les valeurs (une barre, un point). */
export const DIAGRAMMES_A_GLISSER = ['bandes', 'ligne_brisee', 'histogramme', 'nuage'];
const multiple = (v, pas) => Math.abs(v / pas - Math.round(v / pas)) <= 1e-9 * Math.max(1, Math.abs(v / pas));
/**
 * L'AIMANT d'un diagramme qu'on glisse : le pas déclaré par l'exercice ; sinon
 * le pas de la graduation, ou sa moitié, son cinquième, son dixième — le plus
 * grand dont TOUTES les valeurs du diagramme complet sont des multiples (données
 * comme à donner : il ne dit pas laquelle est demandée). `null` : rien ne se
 * glisse, la saisie au clavier reste seule.
 */
export function aimantDuDiagramme(d, declare) {
    if (!DIAGRAMMES_A_GLISSER.includes(d.type))
        return null;
    if (declare !== undefined)
        return declare > 0 ? declare : null;
    const geo = geometrieDuDiagramme(d);
    if (geo.genre !== 'valeurs' && geo.genre !== 'nuage')
        return null;
    const valeurs = elementsDe(d).map((e) => Number(e.valeur));
    for (const diviseur of [1, 2, 5, 10]) {
        const pas = geo.axe.pas / diviseur;
        if (valeurs.every((v) => Number.isFinite(v) && multiple(v, pas)))
            return Number(pas.toFixed(10));
    }
    return null;
}
/** Une valeur glissée, aimantée et gardée sur l'axe. */
export function valeurAimantee(v, aimant, axe) {
    const bas = Math.min(0, axe.de);
    const borne = Math.max(bas, Math.min(axe.a, v));
    return Number((Math.round(borne / aimant) * aimant).toFixed(10));
}
/* ─────────────────────────────── le dessin ──────────────────────────────── */
const n1 = (v) => (Math.round(v * 10) / 10).toString();
const segment = (x1, y1, x2, y2) => `M${n1(x1)} ${n1(y1)} L${n1(x2)} ${n1(y2)}`;
const etiquette = (x, y, t, ton = 'encre', taille = 14) => ({
    type: 'texte',
    x,
    y,
    texte: t,
    taille,
    ancre: 'milieu',
    police: 'etiquette_forte',
    ton,
});
/**
 * Le morceau de la droite `y = a·x + b` qui tient dans le cadre des deux axes,
 * en valeurs. `null` : elle ne le traverse pas.
 */
function dansLeCadre(d, x0, x1, y0, y1) {
    let lo = x0;
    let hi = x1;
    if (Math.abs(d.a) > 1e-12) {
        const xa = (y0 - d.b) / d.a;
        const xb = (y1 - d.b) / d.a;
        lo = Math.max(lo, Math.min(xa, xb));
        hi = Math.min(hi, Math.max(xa, xb));
    }
    else if (d.b < y0 || d.b > y1) {
        return null;
    }
    if (hi < lo)
        return null;
    return [lo, d.a * lo + d.b, hi, d.a * hi + d.b];
}
/** La droite `d`, peinte sur le nuage, bornée à son cadre. */
export function traceDeDroite(geo, d, trait) {
    const m = dansLeCadre(d, geo.axeX.de, geo.axeX.a, Math.min(geo.axe.de, 0), geo.axe.a);
    if (m === null)
        return null;
    return {
        type: 'chemin',
        d: segment(versToile(geo.axeX, m[0]), versToile(geo.axe, m[1]), versToile(geo.axeX, m[2]), versToile(geo.axe, m[3])),
        trait,
    };
}
/** Une croix marquée d'un nom : le point moyen G, et ceux des groupes de Mayer. */
function croix(geo, x, y, nom) {
    const [cx, cy] = [versToile(geo.axeX, x), versToile(geo.axe, y)];
    return [
        {
            type: 'chemin',
            d: `${segment(cx - 7, cy - 7, cx + 7, cy + 7)} ${segment(cx - 7, cy + 7, cx + 7, cy - 7)}`,
            trait: 'main',
        },
        etiquette(cx + 14, cy - 10, nom, 'accent', 15),
    ];
}
/**
 * Insère `dessous` sous les points d'un nuage (avant son premier cercle) — ou,
 * pour un graphe, sous tout le dessin —, et `dessus` à la fin.
 */
function superposer(resolu, dessous, dessus, auDebut = false) {
    const traces = [...resolu.traces];
    const k = auDebut ? 0 : traces.findIndex((t) => t.type === 'cercle');
    traces.splice(k < 0 ? traces.length : k, 0, ...dessous);
    return { ...resolu, traces: [...traces, ...dessus] };
}
/**
 * Le nuage EN COURS : la droite de l'élève, tracée par ses deux poignées (que
 * l'écran dessine lui-même, pour qu'on les saisisse). Ni bande, ni point moyen :
 * rien ne dit, avant la correction, si elle est bien placée.
 */
export function dessinDeDroiteEnCours(d, poignees, decrit) {
    const resolu = resoudreDiagramme(d, decrit);
    const geo = geometrieDuDiagramme(d);
    const droite = poignees === null ? null : droiteDesPoignees(poignees);
    if (geo.genre !== 'nuage' || droite === null)
        return resolu;
    const trace = traceDeDroite(geo, droite, 'encre');
    return trace === null ? resolu : superposer(resolu, [], [trace]);
}
/** Le dessin d'un nuage corrigé : la bande acceptée, la droite de l'élève, le point moyen. */
function dessinDeDroiteCorrigee(c, decrit) {
    const resolu = resoudreDiagramme(c.diagramme, decrit);
    const geo = geometrieDuDiagramme(c.diagramme);
    const dr = c.droite;
    if (geo.genre !== 'nuage' || dr === undefined)
        return resolu;
    const { xMin, xMax, demiLargeur } = dr.bande;
    const ref = (x) => dr.reference.a * x + dr.reference.b;
    const coin = (x, y) => [
        versToile(geo.axeX, x),
        versToile(geo.axe, Math.max(Math.min(y, geo.axe.a), Math.min(geo.axe.de, 0))),
    ];
    const bande = {
        type: 'polygone',
        points: [
            coin(xMin, ref(xMin) - demiLargeur),
            coin(xMax, ref(xMax) - demiLargeur),
            coin(xMax, ref(xMax) + demiLargeur),
            coin(xMin, ref(xMin) + demiLargeur),
        ],
        surface: 'teinte',
    };
    const dessus = [];
    const eleve = dr.eleve === null ? null : traceDeDroite(geo, dr.eleve, 'encre');
    if (eleve !== null)
        dessus.push(eleve);
    dessus.push(...croix(geo, dr.pointMoyen.x, dr.pointMoyen.y, 'G'));
    if (dr.groupes !== undefined) {
        dessus.push(...croix(geo, dr.groupes[0].x, dr.groupes[0].y, 'G₁'));
        dessus.push(...croix(geo, dr.groupes[1].x, dr.groupes[1].y, 'G₂'));
    }
    return superposer(resolu, [bande], dessus);
}
/* ── le graphe ── */
/** Le fond d'un sommet colorié, par numéro de couleur : la couleur n'est jamais seule, le numéro est écrit. */
export const SURFACES_DE_COULEUR = [
    'teinte_forte',
    'papier',
    'jeton_efface',
    'paquet',
    'teinte',
    'jeton_papier',
];
/** Le cercle d'un sommet, de rayon `r`. */
const disque = (x, y, r, surface) => ({
    type: 'polygone',
    points: Array.from({ length: 28 }, (_, i) => {
        const t = (i / 28) * 2 * Math.PI;
        return [x + r * Math.cos(t), y + r * Math.sin(t)];
    }),
    surface,
});
/** Une bande étroite le long d'une arête : ce qui la DÉSIGNE (en ardoise, jamais en rouge). */
function bandeDArete(a, b, demi, surface) {
    const l = Math.max(Math.hypot(b[0] - a[0], b[1] - a[1]), 1e-9);
    const [nx, ny] = [(-(b[1] - a[1]) / l) * demi, ((b[0] - a[0]) / l) * demi];
    return {
        type: 'polygone',
        points: [
            [a[0] + nx, a[1] + ny],
            [b[0] + nx, b[1] + ny],
            [b[0] - nx, b[1] - ny],
            [a[0] - nx, a[1] - ny],
        ],
        surface,
    };
}
/** Le graphe dessiné avec ce que l'élève y a fait : parcours, couleurs, ce qui est désigné. */
export function dessinDuGraphe(g, etat, decrit) {
    const resolu = resoudreDiagramme(g, decrit);
    const geo = geometrieDuDiagramme(g);
    if (g.type !== 'graphe' || geo.genre !== 'graphe')
        return resolu;
    const [dessous, dessus] = calquesDuGraphe(g, geo, etat);
    return superposer(resolu, dessous, dessus, true);
}
function calquesDuGraphe(g, geo, etat) {
    const dessous = [];
    const dessus = [];
    const R = geo.rayon;
    const place = (nom) => geo.places.get(nom);
    const bouts = (de, a) => {
        const p = place(de);
        const q = place(a);
        if (p === undefined || q === undefined)
            return null;
        const l = Math.max(Math.hypot(q[0] - p[0], q[1] - p[1]), 1e-9);
        const [ux, uy] = [(q[0] - p[0]) / l, (q[1] - p[1]) / l];
        return [
            [p[0] + ux * R, p[1] + uy * R],
            [q[0] - ux * R, q[1] - uy * R],
        ];
    };
    /* Le centre du dessin, d'où le poids d'une arête s'écarte : le numéro va de l'autre côté. */
    const haut = g.titre === undefined ? 34 : 64;
    const centre = [geo.largeur / 2, haut + 150];
    if (etat.areteFautive !== undefined && etat.areteFautive !== null) {
        const a = g.aretes[etat.areteFautive];
        const b = a === undefined ? null : bouts(a.de, a.a);
        if (b !== null)
            dessous.push(bandeDArete(b[0], b[1], 9, 'ardoise'));
    }
    for (const [de, a] of etat.manquantes ?? []) {
        const b = bouts(de, a);
        if (b !== null)
            dessous.push({
                type: 'chemin',
                d: segment(b[0][0], b[0][1], b[1][0], b[1][1]),
                trait: 'doux',
            });
    }
    /* Le parcours : chaque arête repassée à la plume, puis son ou ses numéros. */
    const parcours = etat.parcours ?? [];
    const rangs = new Map();
    parcours.forEach((i, k) => rangs.set(i, [...(rangs.get(i) ?? []), k + 1]));
    for (const [i, numeros] of rangs) {
        const a = g.aretes[i];
        const b = a === undefined ? null : bouts(a.de, a.a);
        if (a === undefined || b === null)
            continue;
        dessus.push({ type: 'chemin', d: segment(b[0][0], b[0][1], b[1][0], b[1][1]), trait: 'main' });
        if (etat.numeroter !== true)
            continue;
        const [mx, my] = [(b[0][0] + b[1][0]) / 2, (b[0][1] + b[1][1]) / 2];
        const l = Math.max(Math.hypot(b[1][0] - b[0][0], b[1][1] - b[0][1]), 1e-9);
        const [ux, uy] = [(b[1][0] - b[0][0]) / l, (b[1][1] - b[0][1]) / l];
        const cote = -uy * (mx - centre[0]) + ux * (my - centre[1]) >= 0 ? -1 : 1;
        const [nx, ny] = [mx + cote * -uy * 16, my + cote * ux * 16];
        const texte = numeros.join(', ');
        const largeur = 12 + texte.length * 8;
        dessus.push({
            type: 'rect',
            x: nx - largeur / 2,
            y: ny - 11,
            largeur,
            hauteur: 22,
            rayon: 11,
            surface: 'jeton_accent',
        });
        dessus.push(etiquette(nx, ny + 5, texte, 'encre', 13));
    }
    /* Les sommets coloriés, désignés ou choisis : redessinés par-dessus, le nom réécrit. */
    for (const s of g.sommets) {
        const p = place(s.nom);
        if (p === undefined)
            continue;
        const couleur = etat.couleurs?.[s.nom];
        const fautif = etat.sommetFautif === s.nom;
        const choisi = etat.sommetChoisi === s.nom;
        if (couleur === undefined && !fautif && !choisi)
            continue;
        if (fautif)
            dessous.push(disque(p[0], p[1], R + 8, 'ardoise'));
        if (choisi)
            dessus.push(disque(p[0], p[1], R + 5, 'jeton_accent'));
        const surface = couleur !== undefined && couleur > 0
            ? SURFACES_DE_COULEUR[(couleur - 1) % SURFACES_DE_COULEUR.length]
            : 'teinte';
        dessus.push(disque(p[0], p[1], R, surface));
        dessus.push(etiquette(p[0], p[1] + 5, s.nom));
        if (couleur !== undefined && couleur > 0) {
            /* Le NUMÉRO de la couleur, à côté du sommet : la couleur seule ne dit rien. */
            dessus.push(disque(p[0] + R * 0.9, p[1] + R * 0.9, 9, 'papier'));
            dessus.push(etiquette(p[0] + R * 0.9, p[1] + R * 0.9 + 4, String(couleur), 'accent', 12));
        }
    }
    return [dessous, dessus];
}
/** Le dessin d'un diagramme corrigé : celui de l'élève, et ce que la correction y désigne. */
export function resoudreDiagrammeCorrige(c, decrit) {
    const dit = decrit ?? direDiagrammeCorrige(c);
    if (c.droite !== undefined)
        return dessinDeDroiteCorrigee(c, dit);
    if (c.graphe !== undefined)
        return dessinDuGrapheCorrige(c.graphe, dit);
    return resoudreDiagramme(c.diagramme, dit);
}
function dessinDuGrapheCorrige(gc, decrit) {
    return dessinDuGraphe(gc.graphe, {
        parcours: gc.parcours,
        numeroter: gc.tache === 'chaine_eulerienne' || gc.tache === 'plus_courte_chaine',
        ...(gc.couleurs !== undefined ? { couleurs: gc.couleurs } : {}),
        areteFautive: gc.areteFautive,
        sommetFautif: gc.sommetFautif,
        ...(gc.manquantes !== undefined ? { manquantes: gc.manquantes } : {}),
    }, decrit);
}
/** Le diagramme corrigé dit à qui ne le voit pas : ses données, puis les constats. */
export function direDiagrammeCorrige(c) {
    const base = c.graphe !== undefined ? direDiagramme(c.graphe.graphe) : direDiagramme(c.diagramme);
    const parcours = c.graphe !== undefined && c.graphe.parcours.length > 0
        ? ` Ton parcours : ${c.graphe.parcours
            .map((i, k) => {
            const a = c.graphe?.graphe.aretes[i];
            return `${k + 1}. ${a === undefined ? '?' : `${a.de}–${a.a}`}`;
        })
            .join(' ; ')}.`
        : '';
    const constats = (c.constats ?? []).map((k) => ` ${k.texte}`).join('');
    return `${base}${parcours}${constats}`;
}
/**
 * LA RÉPONSE D'UN DIAGRAMME, EN MOTS — ce que la correction remontre à l'élève
 * sous « Ta réponse » : les éléments nommés, la droite par ses deux poignées, le
 * parcours par ses arêtes. Le dessin corrigé MONTRE le reste.
 */
export function reponseDeDiagrammeEnMots(d, reponse) {
    const lue = lireReponseDeDiagramme(reponse);
    if (lue === null)
        return reponse;
    const elements = elementsDe(d);
    const fr = (v) => String(Number(v.toFixed(2)))
        .replace('.', ',')
        .replace('-', '−');
    const arete = (i) => {
        const a = d.type === 'graphe' ? d.aretes[i] : undefined;
        return a === undefined ? '?' : `${a.de}–${a.a}`;
    };
    const g = lue.graphe;
    const couleurs = Object.entries(g?.couleurs ?? {});
    return [
        ...[...lue.cases].map(([cle, valeur]) => `${elements[Number(cle.split(':')[1])]?.nom ?? cle} : ${valeur}`),
        ...(lue.droite === null
            ? []
            : [
                `ta droite passe par (${fr(lue.droite.x1)} ; ${fr(lue.droite.y1)}) et (${fr(lue.droite.x2)} ; ${fr(lue.droite.y2)})`,
            ]),
        ...((g?.aretes?.length ?? 0) > 0
            ? [`arêtes : ${(g?.aretes ?? []).map(arete).join(', ')}`]
            : []),
        ...(couleurs.length > 0
            ? [`couleurs : ${couleurs.map(([s, c]) => `${s} ${c}`).join(', ')}`]
            : []),
        ...((g?.sommets?.length ?? 0) > 0
            ? [`sommets ajoutés : ${(g?.sommets ?? []).join(', ')}`]
            : []),
        ...((g?.liens?.length ?? 0) > 0
            ? [`arêtes tracées : ${(g?.liens ?? []).map(([a, b]) => `${a}–${b}`).join(', ')}`]
            : []),
    ].join(' ; ');
}
//# sourceMappingURL=manipulation.js.map